import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import collectionApiClient, { CollectionCode } from "../../../api/collection";

const useCollectionCodesInfiniteQuery = (
  textSearch: string,
  limit: number = 10,
  sort?: Record<string, 1 | -1>,
  filter?: Record<string, unknown>,
) => {
  const queryClient = useQueryClient();

  const fetchCollectionCodes = async (
    pageParam: {
      skip?: number;
      limit?: number;
      sort?: Record<string, 1 | -1>;
    },
    queryClient: ReturnType<typeof useQueryClient>,
    query: string,
    filter?: Record<string, unknown>,
  ): Promise<CollectionCode[]> => {
    const { skip = 0, limit, sort } = pageParam;
    const res = await collectionApiClient.getCollectionCodes({
      query,
      skip,
      limit,
      sort,
      filter,
    });

    res.forEach((code) => {
      queryClient.setQueryData(["collectionCode", code.id], code);
    });

    return res;
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } =
    useInfiniteQuery<CollectionCode[], Error>({
      queryKey: ["collectionCodes", textSearch, sort, filter],
      queryFn: ({ pageParam }) =>
        fetchCollectionCodes(
          { ...(pageParam ?? { skip: 0, limit }), sort },
          queryClient,
          textSearch,
          filter,
        ),
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < limit) {
          return undefined;
        }
        const currentLength = allPages.flat()?.length;
        return { skip: currentLength, limit };
      },
      initialPageParam: { skip: 0, limit, sort },
    });

  return {
    data: data?.pages.flat(),
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ...rest,
  };
};

export default useCollectionCodesInfiniteQuery;
